import { generateFullTaxInvoiceHtml } from './quotationTemplateTokens'
import { headerFields, type CompanyProfileLike } from './runningBillInvoice'

type InvoiceData = Parameters<typeof generateFullTaxInvoiceHtml>[0]

/** A saved final tax invoice document (requestType 'invoice', isFinalInvoice) as the API returns it. */
export interface FinalInvoiceDoc {
  number: string
  invoiceNumber?: string
  date?: string
  dueDate?: string
  items?: any[]
  subtotal?: number
  taxTotal?: number
  total?: number
  amountReceived?: number
  gstMode?: 'intra' | 'inter' | 'none'
  gstEnabled?: boolean
  additionalCharges?: number
  additionalChargesLabel?: string
  headerDiscount?: number
  headerDiscountType?: 'percent' | 'fixed'
  clientName?: string
  clientEmail?: string
  clientPhone?: string
  clientAddress?: string
  clientGstin?: string
  clientState?: string
  sourceQuotationNumber?: string
  sourceProformaNumber?: string
  template_data?: {
    header?: Record<string, any>
    finalInvoice?: { bills: Array<{ number: string; stageNumber: number; stageName: string; percent?: number; taxable: number; tax: number; total: number; received?: number }> }
  }
  [k: string]: any
}

/**
 * The heading a tax invoice is printed with. The copy handed out first is the original the recipient
 * keeps; every one after it is a copy, numbered from the second issue — so the downloads recorded on
 * the document decide it: none yet = Original, one = Copy, two = Copy 2, and so on.
 */
export function taxInvoiceCopyLabel(downloadsAlreadyRecorded: number): string {
  const issued = Math.max(0, Math.floor(Number(downloadsAlreadyRecorded) || 0))
  if (issued === 0) return 'Original for Recipient'
  if (issued === 1) return 'Copy'
  return `Copy ${issued}`
}

/**
 * The final tax invoice: the whole contract (every proforma line at 100%), the running bills it adjusts,
 * and nothing left to pay. Header, terms, declaration and signatory follow the proforma, as the running
 * bills do.
 */
export function applyFinalInvoice(
  base: InvoiceData,
  doc: FinalInvoiceDoc,
  context: { company?: CompanyProfileLike; defaultDeclaration?: string } = {},
): InvoiceData {
  const header = { ...(doc.template_data?.header || {}), ...Object.fromEntries(Object.entries(doc).filter(([k, v]) => ['businessName','businessTag','businessAddress','businessGstin','businessPan','businessPhone','businessEmail','businessWeb','businessLogo','declaration','signatory','signatoryRole','footerLeft','footerRight','terms','termsList','notes','poNumber','referenceNo','contactPerson','bankAccName','bankAccNo','bankName','bankBranch','bankIfsc','bankUpi','displayOptions'].includes(k) && v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))) }
  const merged: Record<string, any> = { ...base }
  for (const [k, v] of Object.entries(headerFields(header, context.company, { declaration: context.defaultDeclaration }))) {
    const cur = merged[k]
    if (cur === undefined || cur === null || cur === '' || (Array.isArray(cur) && cur.length === 0)) merged[k] = v
  }
  const bills = doc.template_data?.finalInvoice?.bills || []
  const number = doc.invoiceNumber || doc.number
  return {
    ...(merged as InvoiceData),
    documentNumber: number,
    invoiceNumber: number,
    date: doc.date ? new Date(doc.date).toLocaleDateString('en-IN') : (merged as any).date,
    customerName: (merged as any).customerName || doc.clientName,
    customerEmail: (merged as any).customerEmail || doc.clientEmail,
    customerContact: (merged as any).customerContact || doc.clientPhone,
    customerAddress: (merged as any).customerAddress || doc.clientAddress,
    customerGstin: (merged as any).customerGstin || doc.clientGstin,
    items: (doc.items || []).map((it: any) => ({
      description: it.name,
      subDescription: it.notes || undefined,
      hsn: it.hsnSac || it.hsn,
      qty: it.quantity,
      unit: it.unit,
      rate: it.rate,
      disc: it.discountPercent ?? 0,
      gst: it.taxRate,
    })) as any,
    subtotal: doc.subtotal,
    taxableAmount: doc.subtotal,
    taxTotal: doc.taxTotal,
    grandTotal: doc.total,
    total: doc.total,
    headerDiscount: doc.headerDiscount || undefined,
    headerDiscountType: doc.headerDiscountType,
    additionalCharges: doc.additionalCharges && doc.additionalCharges > 0 ? doc.additionalCharges : undefined,
    additionalChargesLabel: doc.additionalChargesLabel || undefined,
    stageAmountsAreFinal: true,
    gstEnabled: doc.gstEnabled,
    gstMode: doc.gstMode,
    amountReceived: doc.amountReceived ?? doc.total,
    paymentType: 'full_payment',
    finalInvoice: { bills },
    notes: [
      `Quotation Reference: ${doc.sourceQuotationNumber || '—'}`,
      ...(doc.sourceProformaNumber ? [`Proforma Reference: ${doc.sourceProformaNumber}`] : []),
      `Invoice for the completed contract; all ${bills.length} running bills adjusted and fully paid`,
    ],
  }
}
